import React, {useEffect, useState, useRef} from "react";
import {hot} from "react-hot-loader";
import css from "./App.css";
import Footer from "common/Footer";
import StyledButton from "common/StyledButton";
import StickyFooterLayout from "common/StickyFooterLayout";
import Switch from "common/Switch";
import Checkbox from "SF/Checkbox";
import useStarfishAPI from "hooks/useStarfishAPI";
import Toast from "common/Toast";
import {getWord} from "utils/tenant";

const URL = "../user-profile-configurations.html";

const defaultUserProfileConfig = {
    showPronouns: false,
    editPronouns: false
}

/**
 * @returns {JSX.Element}
 * Tenant Admins can enable/disable user profile tenant configurations
 */
const App = () => {
    const [toastMessage, setToastMessage] = useState("");
    const [configUpdateResponse, callConfigUpdateAPI] = useStarfishAPI({ isNewAPI: true });
    const [configFetchResponse, callConfigFetchAPI] = useStarfishAPI({ isNewAPI: true });
    const [userProfileConfig, setUserProfileConfig] = useState(defaultUserProfileConfig);
    const savedConfig = useRef(userProfileConfig);

    const clear = () => {
        setUserProfileConfig(savedConfig.current);
    };

    const submit = () => {
        callConfigUpdateAPI(URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(userProfileConfig)
        });
    };

    useEffect(() => {
        callConfigFetchAPI(URL, {
            method: "GET",
            headers: {
                Pragma: "no-cache",
                "Cache-Control": "no-cache",
                Expires: "-1"
            }
        });
    }, []);

    useEffect(() => {
        if (configFetchResponse.success) {
            setUserProfileConfig(configFetchResponse.data);
            savedConfig.current = configFetchResponse.data;
        } else if (configFetchResponse.error) {
            setToastMessage(configFetchResponse.error);
        }
    }, [configFetchResponse]);

    useEffect(() => {
        if (configUpdateResponse.error) {
            setToastMessage(configUpdateResponse.error);
        } else if (configUpdateResponse.success) {
            savedConfig.current = userProfileConfig;
            setToastMessage("Configuration successfully updated.");
        }
    }, [configUpdateResponse]);

    const getShowStudentPronouns = () => (
        <div className={css["show-student-pronouns"]} data-role="showStudentPronounsSwitch">
            <div className={css["show-student-pronouns-switch"]}>
                <Switch aria-labelledby="showStudentPronouns"
                        checked={userProfileConfig.showPronouns}
                        onChange={(checked) => setUserProfileConfig({...userProfileConfig, showPronouns: checked})} />
                <label id="showStudentPronouns" className={css["user-profile-label"]}>Show students&apos; Pronouns</label>
            </div>
            <div className={css["show-pronouns-help-text"]}>
                This toggle allows Pronouns to display for <b>all users with permissions to view the student</b>.
            </div>
        </div>
    );

    const getEditStudentPronouns = () => (
        <div className={css["edit-student-pronouns"]}>
            <Checkbox aria-labelledby="editStudentPronouns"
                      checked={userProfileConfig.editPronouns}
                      onChange={(checked) => setUserProfileConfig({...userProfileConfig, editPronouns: checked})} />
            <label id="editStudentPronouns" className={css["user-profile-label"]}>Allow students to modify Pronouns</label>
            <div className={css["edit-pronouns-help-text"]} role="text">
                If enabled, students can add, edit, and delete their pronouns in their {getWord("Starfish", false)} profile. Otherwise, pronouns will be read only.
                Updates in the users file will overwrite student entries. If disabled and you want to remove students&apos; manual entries, contact {getWord("Starfish", false)} Support.
                Please note that student modifications in {getWord("Starfish", false)} will not alter your SIS data.
            </div>
        </div>
    );

    const getShowStudentLegalFirstName = () => (
        <div className={css["show-student-pronouns"]} data-role="showStudentLegalFirstNameSwitch">
            <div className={css["show-student-pronouns-switch"]}>
                <Switch aria-labelledby="showStudentLegalFirstName"
                        checked={userProfileConfig.showLegalFirstName}
                        onChange={(checked) => setUserProfileConfig({...userProfileConfig, showLegalFirstName: checked})} />
                <label id="showStudentLegalFirstName" className={css["user-profile-label"]}>Show students&apos; Legal First Name</label>
            </div>
            <div className={css["show-pronouns-help-text"]}>
                This toggle allows Legal First Name to display for <b>all users with permissions to view the Student Folder</b>.
            </div>
        </div>
    );

    const getContent = () => (
        <div className={css["container"]}>
            <div className={css["title"]}>User Profile</div>
            <hr/>
            <div className={css["user-profile-content"]}>
                <div className={css["section-title"]}>Legal First Name</div>
                {getShowStudentLegalFirstName()}
            </div>
            <div className={css["user-profile-content"]}>
                <div className={css["section-title"]}>Pronouns</div>
                {getShowStudentPronouns()}
                {userProfileConfig.showPronouns && getEditStudentPronouns()}
            </div>
            {toastMessage && <Toast message={toastMessage} onTimeout={() => setToastMessage("")} />}
        </div>
    );

    const getFooter = () => {
        return (
            <Footer
                primaryButton={<StyledButton onClick={submit}>Save Changes</StyledButton>}
                secondaryButton={<StyledButton asLink onClick={clear}>Cancel</StyledButton>}
            />
        )
    };

    return <StickyFooterLayout content={getContent()} footer={getFooter()} />

};

export default hot(module)(App);
